import React, { useId } from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface FieldProps {
  label?: React.ReactNode;
  /** Right-aligned element on the label row, e.g. a "Forgot password?" link. */
  labelAction?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  /**
   * The control. A single element receives id, aria-describedby and aria-invalid
   * automatically; pass a render function to wire them yourself.
   */
  children:
    | React.ReactElement
    | ((ids: { id: string; describedBy?: string; invalid: boolean }) => React.ReactNode);
}

/** Label + control + hint/error with the accessibility attributes wired up. */
export function Field({
  label,
  labelAction,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;
  const invalid = Boolean(error);

  const control =
    typeof children === "function"
      ? children({ id, describedBy, invalid })
      : React.cloneElement(children as React.ReactElement<any>, {
          id: (children.props as any).id ?? id,
          "aria-describedby": describedBy,
          "aria-invalid": invalid || undefined,
          required: (children.props as any).required ?? required,
        });

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {(label || labelAction) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <Label htmlFor={id}>
              {label}
              {required && (
                <span className="ml-0.5 text-muted-foreground" aria-hidden>
                  *
                </span>
              )}
            </Label>
          )}
          {labelAction}
        </div>
      )}
      {control}
      {error ? (
        <p id={errorId} className="text-caption text-destructive" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-caption text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
